import * as React from "react";
import { connect } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import uuid from "uuid/v4";
import lodashDebounce from "lodash/debounce";
import { targetValuesGridStyles } from "./styles";
import {
    ColDef,
    GridReadyEvent,
    ICellRendererParams,
    ProcessDataFromClipboardParams,
    RowHeightParams,
    ValueGetterParams,
    ValueSetterParams,
    EditableCallbackParams,
    GridApi,
    GetRowIdParams,
    CellPosition,
    CellKeyDownEvent,
} from "ag-grid-community";
import { get, isNumber, isString } from "lodash";
import lodashCloneDeep from "lodash/cloneDeep";
import { formatMessage } from "lib/globalize-util";
import theme from "styles/theme";
import { IStore } from "reducers";
import { DimensionsPanelReduxState, DimensionValue } from "reducers/dimensions-panel/dimensions-panel";
import { DATATEST } from "constants/integration";
import { TARGET_BAG_ID } from "constants/product-collection";
import PasteCellRenderer from "./cell-renderers/PasteCellRenderer";
import { DoorIdMap, PasteError, TargetRowData } from "./paste-container";
import { KEYBOARD_KEYS } from "constants/keyboard";
import { resetSelectedCellRanges } from "helpers/ag-grid";
import { getMetricsValueFormatter } from "../../../../../../../helpers/metrics/formatters/metrics-formatter-helper";
import { metricsNumberFormatter } from "../../../../../../../helpers/metrics/formatters/metrics-number-formatter";
import { Metric } from "../../../../../../../helpers/metrics/types";
const PINNED_TOTALS_IDENTIFIER = "PINNED_TOTALS_IDENTIFIER";
export const BLANK_ROW_IDENTIFIER = "BLANK_ROW_IDENTIFIER";

const styles = targetValuesGridStyles();

interface IProps {
    targetRowData: TargetRowData[];
    setTargetRowData: (targetRowData: TargetRowData[]) => void;
    setError: (error: PasteError) => void;
    doorIdMap: DoorIdMap;
    selectedMetrics: Metric | undefined;
}

type ReduxProps = {
    draft: DimensionsPanelReduxState;
};

/**
 * Calculate the total target value for current target
 * @param targetRowData - row data
 * @param deliveryId - optional delivery id
 */
export const generateTotal = (targetRowData: TargetRowData[], deliveryId?: string): number => {
    let total = 0;

    for (const row of targetRowData) {
        if (row.doorId && row.deliveryIdMap) {
            for (const key of Object.keys(row.deliveryIdMap)) {
                const val = row.deliveryIdMap[key];
                if (isValidTargetValue(val)) {
                    if (deliveryId && deliveryId === key) {
                        total += parseFloat(val);
                    } else if (!deliveryId) {
                        total += parseFloat(val);
                    }
                }
            }
        }
    }

    return total;
};

export const valueSetterFactory = (metric: Metric) => {
    return (params: ValueSetterParams) => {
        let { newValue } = params;
        const { colDef, data } = params;

        const deliveryId = colDef.colId;

        if (!newValue) {
            delete data.deliveryIdMap?.[deliveryId];
            return true;
        }

        // We want to make sure that we only save the decimals specified in the number format for this metric
        // If it doesn't have any decimals, then just save as is
        const numNewValue = Number(newValue);
        if (numNewValue && newValue.indexOf(".") !== -1 && metric?.numberFormat) {
            let decimalPlaces = 0;
            if (metric.numberFormat.currency) {
                decimalPlaces = metric.numberFormat.currency.decimal_places;
            } else if (metric.numberFormat.percentage) {
                decimalPlaces = metric.numberFormat.percentage.decimal_places;
            }
            newValue = numNewValue.toFixed(decimalPlaces).toString();
        }

        if (data.deliveryIdMap) {
            data.deliveryIdMap[deliveryId] = newValue;
        } else {
            data.deliveryIdMap = {
                [deliveryId]: newValue,
            };
        }

        return true;
    };
};

export const isValidTargetValue = (value: any): boolean => {
    return value === "" || value === undefined || (isNumber(+value) && +value > -1);
};

/**
 * Checks to see if a door is contained with the current merchant active doors and is not in the current existing rows
 * @param value - internal door id
 * @param doorIdMap
 * @param existingRows
 * @param currentDoorId
 */
export const isValidDoor = (
    value: any,
    doorIdMap: DoorIdMap,
    existingRows: TargetRowData[],
    currentDoorId: string
): boolean => {
    return (
        isString(value) &&
        !!doorIdMap.external[value] &&
        !existingRows.find((row) => row.externalDoorId === value && row.doorId !== currentDoorId)
    );
};

export const INVALID_VALUE = -1;

export const TargetValuesGridComponent: React.FunctionComponent<IProps & ReduxProps> = ({
    draft,
    targetRowData,
    setTargetRowData,
    setError,
    doorIdMap,
    selectedMetrics,
}) => {
    const gridApi = React.useRef<GridApi>();
    // Needed to have the latest state for
    const mutableRowData = React.useRef([]);

    React.useEffect(() => {
        mutableRowData.current = lodashCloneDeep(targetRowData);

        if (gridApi.current) {
            const currentFocusedCell: CellPosition = gridApi.current.getFocusedCell();
            gridApi.current.setRowData(mutableRowData.current);
            process.nextTick(() => {
                gridApi.current.setFilterModel(null);
                gridApi.current.setFocusedCell(currentFocusedCell.rowIndex, currentFocusedCell.column.getColId());
            });
        }
    }, [targetRowData]);

    const deliveryIds = React.useMemo(() => {
        const dimensions = draft.dimensions;
        let outerDimensionValues: DimensionValue[] = [];
        if (dimensions.length === 2) {
            outerDimensionValues = dimensions[0].values;
        }
        return outerDimensionValues;
    }, [draft]);

    const columnDefs = React.useMemo<ColDef[]>(() => {
        const colDefs: ColDef[] = [];
        const valueFormatter = getMetricsValueFormatter({
            kind: selectedMetrics?.kind,
            numberFormat: selectedMetrics?.numberFormat,
        });
        const valueSetter = valueSetterFactory(selectedMetrics);

        colDefs.push({
            headerName: formatMessage("DOOR #"),
            colId: "externalDoorId",
            hide: false,
            editable: (params: EditableCallbackParams) => {
                const type = get(params, "data.type");
                return type !== PINNED_TOTALS_IDENTIFIER;
            },
            valueGetter: (getterParams: ValueGetterParams) => {
                const { data } = getterParams;
                return data.externalDoorId;
            },
            cellRendererSelector: (params: ICellRendererParams) => {
                const externalDoorId = get(params, "data.externalDoorId");
                const type = get(params, "data.type");
                if (externalDoorId || type === PINNED_TOTALS_IDENTIFIER) {
                    return undefined;
                } else if (type === BLANK_ROW_IDENTIFIER) {
                    return { component: PasteCellRenderer };
                }
            },
            cellClass: (params) => {
                const type = get(params, "data.type");
                const externalDoorId = get(params, "data.externalDoorId");
                const doorId = get(params, "data.doorId");
                if (type === PINNED_TOTALS_IDENTIFIER) {
                    return "doorsGroupTotal--notEditable";
                }

                if (type === BLANK_ROW_IDENTIFIER) {
                    return undefined;
                }

                // Check to make sure this is a valid door
                return !isValidDoor(externalDoorId, doorIdMap, mutableRowData.current, doorId)
                    ? "errorTargetValue"
                    : undefined;
            },
            valueSetter: (params: ValueSetterParams) => {
                const { newValue, data } = params;
                data.externalDoorId = newValue;
                return true;
            },
            filter: "agSetColumnFilter",
            menuTabs: ["filterMenuTab", "generalMenuTab"],
        });

        const dimensions = draft.dimensions;
        let outerDimensionValues: DimensionValue[] = [];
        if (dimensions.length === 2) {
            outerDimensionValues = dimensions[0].values;
        }

        outerDimensionValues.forEach((value) => {
            colDefs.push({
                headerName: value.label,
                colId: value.id,
                hide: false,
                editable: (params: EditableCallbackParams) => {
                    const type = get(params, "data.type");
                    return type !== PINNED_TOTALS_IDENTIFIER;
                },
                cellClass: (params) => {
                    const { colDef, data } = params;
                    const type = get(params, "data.type");
                    if (type === PINNED_TOTALS_IDENTIFIER) {
                        return "doorsGroupTotal--notEditable";
                    } else {
                        const deliveryId = colDef.colId;
                        const val = data.deliveryIdMap?.[deliveryId];
                        return !isValidTargetValue(val) ? "errorTargetValue" : undefined;
                    }
                },
                valueGetter: (getterParams: ValueGetterParams) => {
                    const { colDef, data } = getterParams;
                    const deliveryId = colDef.colId;
                    const type = get(getterParams, "data.type");

                    if (type === PINNED_TOTALS_IDENTIFIER) {
                        const total = generateTotal(targetRowData, deliveryId);
                        return isNumber(total) && !isNaN(total) && total > 0 ? total : undefined;
                    } else {
                        return data.deliveryIdMap?.[deliveryId];
                    }
                },

                valueSetter: valueSetter,
                valueFormatter: valueFormatter,
            });
        });

        return colDefs;
    }, [doorIdMap, draft, targetRowData, selectedMetrics]);

    const processDataFromClipboard = (params: ProcessDataFromClipboardParams) => {
        const { data, api, columnApi } = params;

        if (!api) {
            return undefined;
        }

        const lastIndex = api.getModel().getRowCount() - 1;
        const focusedCell = api.getFocusedCell();
        const focusedIndex = focusedCell.rowIndex;
        const numberOfColumnsInRowData = data[0]?.length ?? 0;
        const numberOfColumnsInGrid = columnApi.getAllGridColumns().length;

        if (numberOfColumnsInRowData > numberOfColumnsInGrid) {
            setError(PasteError.ColumnCount);
            return undefined;
        }

        if (focusedIndex + params.data.length - 1 > lastIndex) {
            const clonedTargetRows = mutableRowData.current ? lodashCloneDeep(mutableRowData.current) : [];
            for (const rowDataFromPaste of data) {
                const rowExternalDoorId = rowDataFromPaste[0];
                if (rowExternalDoorId) {
                    for (let i = 1; i < rowDataFromPaste.length; i++) {
                        const deliveryIndex = i - 1;
                        const deliveryIdValue = rowDataFromPaste[i];
                        const deliveryId = deliveryIds[deliveryIndex];
                        if (deliveryId) {
                            let foundRow = false;
                            for (const row of clonedTargetRows) {
                                if (row.externalDoorId === rowExternalDoorId && rowExternalDoorId) {
                                    if (row.deliveryIdMap) {
                                        row.deliveryIdMap[deliveryId.id] = deliveryIdValue;
                                    } else {
                                        row.deliveryIdMap = {
                                            [deliveryId.id]: deliveryIdValue,
                                        };
                                    }
                                    foundRow = true;
                                }
                            }

                            if (!foundRow) {
                                clonedTargetRows.push({
                                    // Since this is new row, we don't know the internal id yet. Generate id as placeholder
                                    doorId: uuid(),
                                    externalDoorId: rowExternalDoorId,
                                    isNew: true,
                                    deliveryIdMap: {
                                        [deliveryId.id]: deliveryIdValue,
                                    },
                                });
                            }
                        }
                    }
                }
            }

            setError(null);
            setTargetRowData([
                ...clonedTargetRows.filter((row) => row.type !== BLANK_ROW_IDENTIFIER),
                { type: BLANK_ROW_IDENTIFIER },
            ]);
            return undefined;
        }

        setError(null);
        return data;
    };

    const getRowHeight = (params: RowHeightParams) => {
        const type = get(params, "data.type");
        if (type === PINNED_TOTALS_IDENTIFIER) {
            return 25;
        } else {
            return 40;
        }
    };

    const total = React.useMemo(() => {
        return generateTotal(targetRowData);
    }, [targetRowData]);

    const onGridReady = (event: GridReadyEvent) => {
        const { columnApi, api } = event;
        const firstEditCol = columnApi.getAllDisplayedColumns()[0];
        api.ensureColumnVisible(firstEditCol);
        api.setFocusedCell(api.getDisplayedRowCount() - 1, firstEditCol);
        gridApi.current = api;
    };

    const updateTargetRowData = lodashDebounce(() => {
        if (mutableRowData.current) {
            // TODO: TARGET move this to helper function
            let blankRowPendingExternalId;
            if (gridApi?.current) {
                const blankRowPendingData = gridApi?.current.getRowNode(BLANK_ROW_IDENTIFIER)?.data;
                blankRowPendingExternalId = blankRowPendingData.externalDoorId;
            }

            const updatedTargetRowData = mutableRowData.current.reduce((acc, row) => {
                const mutatedRow = lodashCloneDeep(row);

                if (mutatedRow.type === BLANK_ROW_IDENTIFIER && blankRowPendingExternalId) {
                    mutatedRow.externalDoorId = blankRowPendingExternalId;
                }

                const externalIdNotEmpty = !!mutatedRow.externalDoorId;
                const quantitiesNotEmpty = Object.values(mutatedRow.deliveryIdMap ?? {}).some((quantity) => !!quantity);
                const notEmptyRow = externalIdNotEmpty || quantitiesNotEmpty;

                // Ignore row that's empty
                if (!notEmptyRow) {
                    return acc;
                }
                // If it was blank row and now it has value
                if (mutatedRow.type === BLANK_ROW_IDENTIFIER && notEmptyRow) {
                    mutatedRow.isNew = true;

                    // Unsetting this is solely for unsetting BLANK_ROW_IDENTIFIER
                    mutatedRow.type = undefined;

                    // Generate placeholder internal id. This will be replaced downstream
                    mutatedRow.doorId = uuid();
                }

                acc.push(mutatedRow);

                return acc;
            }, []);

            setTargetRowData(updatedTargetRowData.concat([{ type: BLANK_ROW_IDENTIFIER }]));
        }
    }, 50);

    const handleCellChange = React.useCallback(() => {
        updateTargetRowData();
    }, [updateTargetRowData]);

    const getRowId = React.useCallback(({ data }: GetRowIdParams) => {
        if (data.type === BLANK_ROW_IDENTIFIER) {
            return BLANK_ROW_IDENTIFIER;
        }

        return data.doorId;
    }, []);

    const handleCellKeyDown = ({ event }: CellKeyDownEvent) => {
        const typedEvent = event as KeyboardEvent;
        const eventKey = typedEvent.key;
        const isDeletionKey = KEYBOARD_KEYS.BACKSPACE === eventKey || KEYBOARD_KEYS.DELETE === eventKey;

        if (isDeletionKey) {
            resetSelectedCellRanges(gridApi.current);
        }
    };

    const defaultColDef = {
        menuTabs: ["generalMenuTab"],
    };

    return (
        <div {...styles} className="targetValueContainer">
            <div
                className={`${theme.agGrid.defaultTheme} grid`}
                data-test={DATATEST.ASSORTMENTS.FOOTER.MANAGE_TARGETS.ADD_TARGET_VALUES_CONTAINER.GRID}
            >
                <AgGridReact
                    onGridReady={onGridReady}
                    rowDragManaged
                    debug={false}
                    rowData={mutableRowData.current}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    enableRangeSelection
                    enableCellChangeFlash
                    pinnedTopRowData={[
                        {
                            type: PINNED_TOTALS_IDENTIFIER,
                        },
                    ]}
                    animateRows
                    suppressCopyRowsToClipboard
                    stopEditingWhenCellsLoseFocus
                    enterMovesDownAfterEdit
                    getRowHeight={getRowHeight}
                    sideBar={false}
                    suppressAggFuncInHeader={true}
                    suppressContextMenu={true}
                    processDataFromClipboard={processDataFromClipboard}
                    onCellValueChanged={handleCellChange}
                    getRowId={getRowId}
                    onCellKeyDown={handleCellKeyDown}
                />
            </div>
            <div className="totalContainer">{`${formatMessage("Total")}: ${metricsNumberFormatter({
                kind: selectedMetrics?.kind,
                numberFormat: selectedMetrics?.numberFormat,
            })(total)}`}</div>
        </div>
    );
};

const mapStateToProps = (state: IStore) => {
    const targetAllocationBag = state.mutate.snapshots && state.mutate.snapshots.get(TARGET_BAG_ID);
    return {
        targetAllocationBag,
        draft: state.dimensionsPanel.draft,
    };
};

export const TargetValuesGrid = connect<ReduxProps, {}, IProps>(mapStateToProps, {})(TargetValuesGridComponent);
